import { ReactElement, useCallback, useEffect, useState } from 'react'
import { readSupportHomesHomeLogs } from '@racepointenergy/swc/scs/read-support-homes-home-logs'
import { updateSupportHomesHomeLogsUpload } from '@racepointenergy/swc/scs/update-support-homes-home-logs-upload'
import { Prompt } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'

import useTimeoutPoll from 'app/utils/useTimeoutPoll'
import { Home } from 'features/home/types'
import { CommonButton } from 'components/Button'
import {
  HomeLogsButton,
  HomeLogsSpinnerContainer,
  StyledHomeLogsHeader,
  StyledHomeLogsInfo,
  StyledLogsContentBtnsWrap,
} from '../styles'
import SuccessModal from 'components/Modal/SuccessModal'
import {
  logsUnsupportedLinuxHostVersion,
  logsUnsupportedMacHostVersion,
} from '../utils'
import { homeLogsMessages } from '../messages'
import { commonMessages } from 'app/i18n/messages'
import HomeLogsInner from '../HomeLogsInner'
import { SpinnerCentered } from 'components/Loader'

const FOUR_SECONDS = 1000 * 4
const SEVEN_MINUTES = 1000 * 60 * 7

interface HomeLogsContentProps {
  home: Home
}

function HomeLogsContent({ home }: HomeLogsContentProps) {
  const { formatMessage } = useIntl()

  const [showTimeout, setShowTimeout] = useState(false)
  const [errorCode, setErrorCode] = useState(null)

  const handleShowTimeout = useCallback(() => setShowTimeout(true), [])

  const [polling, setPolling] = useTimeoutPoll(SEVEN_MINUTES, handleShowTimeout)

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const { data, isError, isFetching, isLoading, isSuccess, error, refetch } =
    readSupportHomesHomeLogs.useQuery(
      { homeId: home.id, location: true },
      { pollingInterval: polling ? FOUR_SECONDS : 0 }
    )

  const logsLength = data?.length ?? 0

  useEffect(
    function stopPollingIfLogsChange() {
      setPolling(false)
    },
    [logsLength, setPolling]
  )

  useEffect(
    function stopPollingOnError() {
      if (isError) {
        setPolling(false)
      }
    },
    [isError, setPolling]
  )

  const [triggerHomeLogsUpload] = updateSupportHomesHomeLogsUpload.useMutation()

  const hostType = home.hostType
  const versionNumber = home.softwareInfo?.version.number || 0

  const unsupportedMacHostVersion = logsUnsupportedMacHostVersion(
    hostType,
    versionNumber
  )
  const unsupportedLinuxHostVersion = logsUnsupportedLinuxHostVersion(
    hostType,
    versionNumber
  )

  const monitorAccessEnabled = home.monitorAccessEnabled

  const shouldDisableButtons =
    isFetching ||
    isLoading ||
    polling ||
    unsupportedMacHostVersion ||
    unsupportedLinuxHostVersion ||
    !monitorAccessEnabled

  const renderFetchButton = (): ReactElement => {
    const handleFetchClick = async () => {
      try {
        const response = await triggerHomeLogsUpload({ homeId: home.id })
        /// @ts-ignore
        if (response.error) {
          /// @ts-ignore
          setErrorCode(response.error.data.error.code)
          return
        }
        setPolling(true)
      } catch (error) {
        console.error(error)
      }
    }

    return (
      <HomeLogsButton
        variant="primary"
        onClick={handleFetchClick}
        disabled={shouldDisableButtons}
      >
        {polling
          ? formatMessage(homeLogsMessages.fetching)
          : formatMessage(homeLogsMessages.requestNew)}
      </HomeLogsButton>
    )
  }

  if (isLoading) {
    return (
      <HomeLogsSpinnerContainer>
        <SpinnerCentered size="40" />
      </HomeLogsSpinnerContainer>
    )
  }

  return (
    <>
      <Prompt
        when={polling}
        message={formatMessage(homeLogsMessages.fetchingLeave)}
      />
      <div>
        {errorCode && (
          <SuccessModal
            title={formatMessage(commonMessages.error)}
            isOpen
            confirmBtnTitle={formatMessage(commonMessages.ok)}
            onConfirm={() => setErrorCode(null)}
          >
            {formatMessage(
              errorCode === 234
                ? homeLogsMessages.notAvailableHost
                : commonMessages.commonError
            )}
          </SuccessModal>
        )}
        {showTimeout && (
          <>
            <SuccessModal
              title={formatMessage(homeLogsMessages.timedOutTitle)}
              isOpen={showTimeout}
              confirmBtnTitle={formatMessage(commonMessages.ok)}
              onConfirm={() => setShowTimeout(false)}
            />
            <div>
              <FormattedMessage {...homeLogsMessages.timedOut} />
              <CommonButton onClick={() => setShowTimeout(false)}>
                <FormattedMessage {...commonMessages.ok} />
              </CommonButton>
            </div>
          </>
        )}

        <StyledHomeLogsHeader>
          <StyledHomeLogsInfo>
            <h1>
              <FormattedMessage {...homeLogsMessages.pageTitle} />
            </h1>
          </StyledHomeLogsInfo>

          <StyledLogsContentBtnsWrap>
            <CommonButton
              variant="secondary"
              onClick={refetch}
              disabled={shouldDisableButtons}
            >
              <FormattedMessage {...commonMessages.refresh} />
            </CommonButton>

            {renderFetchButton()}
          </StyledLogsContentBtnsWrap>
        </StyledHomeLogsHeader>
        <HomeLogsInner
          home={home}
          data={data || []}
          isFetching={isFetching}
          unsupportedMacHostVersion={unsupportedMacHostVersion}
          unsupportedLinuxHostVersion={unsupportedLinuxHostVersion}
          monitorAccessEnabled={monitorAccessEnabled}
        />
      </div>
    </>
  )
}

export default HomeLogsContent
